import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminPage, Pill, statusTone } from "@/pages/admin/AdminLayout";

const STATUSES = ["RESEARCHING", "READY_TO_APPLY", "APPLIED", "APPROVED", "ACTIVE", "PAUSED", "REJECTED"];
const HEALTH = [
  ["green", "Active and tested"],
  ["yellow", "Terms need reverification"],
  ["orange", "Paused"],
  ["red", "Broken / inactive"],
];
const TIERS = ["direct", "specialty", "major"];

const TEXT_FIELDS = [
  ["merchant", "Merchant"],
  ["network", "Network"],
  ["program_url", "Program URL"],
  ["destination_url", "Destination URL"],
  ["tracking_url", "Affiliate tracking URL"],
  ["commission_type", "Commission type"],
  ["disclosure", "Disclosure information"],
  ["application_requirements", "Application requirements"],
  ["traffic_requirements", "Traffic requirements"],
  ["content_restrictions", "Content restrictions"],
  ["trademark_bidding", "Trademark bidding restrictions"],
  ["coupon_restrictions", "Coupon restrictions"],
  ["image_permissions", "Image permissions"],
  ["conflicting_terms", "Conflicting terms"],
  ["notes", "Notes"],
];

// Public card presentation. A category page only renders a partner once these are filled in and
// the record is ACTIVE + Active with a tracking URL.
const CARD_FIELDS = [
  ["display_name", "Card title (product or collection name)"],
  ["description", "Card description"],
  ["cta_label", "Button label"],
  ["image", "Product or partner image URL"],
  ["image_alt", "Image alt text"],
];

const NUM_FIELDS = [
  ["commission_rate", "Commission rate (%)"],
  ["cookie_days", "Cookie / attribution window (days)"],
  ["average_order_value", "Average order value"],
  ["priority", "Priority (lower = preferred)"],
];

const BOOL_FIELDS = [
  ["active", "Active"],
  ["seo_allowed", "SEO allowed"],
  ["email_allowed", "Email allowed"],
  ["social_allowed", "Social allowed"],
  ["ppc_allowed", "PPC allowed"],
  ["content_preapproval_required", "Content preapproval required"],
  ["new_publisher_friendly", "New-publisher friendly"],
];

export default function OffersAdmin() {
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = () => api.get("/admin/offers").then((r) => setOffers(r.data));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setCats(r.data.offer_categories || []));
  }, []);

  const save = async (id, body) => {
    const { id: _id, created_at, updated_at, ...payload } = body;
    try {
      const { data } = await api.patch(`/admin/offers/${id}`, payload);
      setOffers((os) => os.map((o) => (o.id === id ? data : o)));
      toast.success("Offer updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save offer");
    }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/admin/offers", {
        merchant: "New merchant record",
        network: "TBD",
        tier: "specialty",
        categories: [],
        status: "RESEARCHING",
        notes: "No relationship exists. Terms unverified.",
      });
      setOffers((o) => [...o, data]);
      setOpenId(data.id);
      setDraft(data);
      toast.success("Offer record created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create offer");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/offers/${id}`);
      setOffers((os) => os.filter((o) => o.id !== id));
      if (openId === id) setOpenId(null);
      toast.success("Offer deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete offer");
    }
  };

  return (
    <AdminPage
      title="Affiliate offers"
      lede="The central affiliate offer database. Articles and tools never hard-code a merchant — they request a category and this table decides which offer resolves, using the direct → specialty → major fallback hierarchy. If nothing is ACTIVE with a tracking URL, the public CTA is disabled automatically."
      actions={
        <button
          onClick={create}
          data-testid="offer-create"
          className="px-7 py-3.5 bg-charcoal text-white text-[11.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors"
        >
          New offer record
        </button>
      }
    >
      <div className="bg-white border border-line overflow-x-auto" data-testid="admin-offers-table">
        <table className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="bg-bone border-b border-line">
              {["Health", "Merchant", "Network", "Tier", "Categories", "Status", "Tracking URL", "Terms verified", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[9.5px] tracking-[0.16em] uppercase text-[#6E6A63] font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id} className="border-b border-line/70" data-testid={`offer-row-${o.id}`}>
                <td className="px-4 py-3.5">
                  <span
                    data-testid={`offer-health-${o.id}`}
                    title={HEALTH.find((h) => h[0] === o.link_health)?.[1]}
                    className="inline-block w-2.5 h-2.5 rounded-full"
                    style={{
                      background: { green: "#22C55E", yellow: "#EAB308", orange: "#F97316", red: "#EF4444" }[
                        o.link_health
                      ],
                    }}
                  />
                </td>
                <td className="px-4 py-3.5 text-[13.5px] text-charcoal max-w-[240px]">{o.merchant}</td>
                <td className="px-4 py-3.5 text-[12.5px] font-light text-[#5A5A5A]">{o.network}</td>
                <td className="px-4 py-3.5 text-[12px] font-light text-[#5A5A5A] uppercase tracking-wide">{o.tier}</td>
                <td className="px-4 py-3.5 text-[11.5px] font-light text-[#5A5A5A] max-w-[220px]">
                  {(o.categories || []).join(", ") || "—"}
                </td>
                <td className="px-4 py-3.5">
                  <Pill tone={statusTone(o.status)}>{o.status.replace(/_/g, " ")}</Pill>
                </td>
                <td className="px-4 py-3.5 text-[12px] font-light">
                  {o.tracking_url ? (
                    <span className="text-[#166534]">set</span>
                  ) : (
                    <span className="text-[#991B1B]">none — CTA disabled</span>
                  )}
                </td>
                <td className="px-4 py-3.5 text-[12px] font-light">
                  {o.last_terms_verified ? (
                    <span className="text-[#166534]">{String(o.last_terms_verified).slice(0, 10)}</span>
                  ) : (
                    <span className="text-[#B4741F]">not verified</span>
                  )}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <button
                    onClick={() => {
                      setOpenId(openId === o.id ? null : o.id);
                      setDraft(o);
                    }}
                    data-testid={`offer-edit-${o.id}`}
                    className="text-[11.5px] tracking-[0.12em] uppercase text-sage hover:text-charcoal transition-colors mr-4"
                  >
                    {openId === o.id ? "Close" : "Edit"}
                  </button>
                  <button
                    onClick={() => remove(o.id)}
                    data-testid={`offer-delete-${o.id}`}
                    className="text-[11.5px] tracking-[0.12em] uppercase text-[#991B1B]/70 hover:text-[#991B1B] transition-colors"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openId && (
        <div className="bg-white border border-line mt-8 px-6 md:px-8 py-8" data-testid={`offer-editor-${openId}`}>
          <h2 className="display text-[26px] text-charcoal mb-6">{draft.merchant}</h2>

          <div className="grid md:grid-cols-3 gap-5 mb-7">
            <label className="block">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Status</span>
              <select
                value={draft.status}
                onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                data-testid="offer-status-select"
                className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
              >
                {STATUSES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Link health</span>
              <select
                value={draft.link_health}
                onChange={(e) => setDraft({ ...draft, link_health: e.target.value })}
                data-testid="offer-health-select"
                className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
              >
                {HEALTH.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Tier</span>
              <select
                value={draft.tier}
                onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
                className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
              >
                {TIERS.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="mb-7">
            <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Eligible categories</span>
            <div className="flex flex-wrap gap-2 mt-2.5">
              {cats.map((c) => {
                const on = (draft.categories || []).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        categories: on
                          ? draft.categories.filter((x) => x !== c)
                          : [...(draft.categories || []), c],
                      })
                    }
                    data-testid={`offer-cat-${c}`}
                    className={`px-3 py-1.5 text-[11.5px] border transition-colors ${
                      on ? "bg-sage border-sage text-white" : "border-line bg-white text-[#5A5A5A] hover:border-sage/50"
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-7 border border-line bg-bone px-5 py-5">
            <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
              Public card presentation
            </span>
            <p className="text-[12px] font-light text-[#6E6A63] mt-1.5 leading-relaxed">
              Fill these in to publish an approved partner on its category page. Leave them empty
              and the record stays internal — nothing is shown to visitors.
            </p>
            <div className="grid md:grid-cols-2 gap-5 mt-4">
              {CARD_FIELDS.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">{label}</span>
                  <input
                    value={draft[k] || ""}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                    data-testid={`offer-field-${k}`}
                    className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white focus:outline-none focus:border-sage"
                  />
                </label>
              ))}
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Card type</span>
                <select
                  value={draft.kind || "collection"}
                  onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                  data-testid="offer-kind-select"
                  className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
                >
                  <option value="collection">collection</option>
                  <option value="product">product</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">Image fit</span>
                <select
                  value={draft.image_fit || "cover"}
                  onChange={(e) => setDraft({ ...draft, image_fit: e.target.value })}
                  className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
                >
                  <option value="cover">cover</option>
                  <option value="contain">contain</option>
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
                  Display order (lower shows first)
                </span>
                <input
                  type="number"
                  value={draft.display_order ?? ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      display_order: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
                />
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {TEXT_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">{label}</span>
                <input
                  value={draft[k] || ""}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  data-testid={`offer-field-${k}`}
                  className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white focus:outline-none focus:border-sage"
                />
              </label>
            ))}
            {NUM_FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">{label}</span>
                <input
                  type="number"
                  value={draft[k] ?? ""}
                  onChange={(e) =>
                    setDraft({ ...draft, [k]: e.target.value === "" ? null : Number(e.target.value) })
                  }
                  className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white focus:outline-none focus:border-sage"
                />
              </label>
            ))}
            <label className="block">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
                Last terms verification date
              </span>
              <input
                type="date"
                value={(draft.last_terms_verified || "").slice(0, 10)}
                onChange={(e) => setDraft({ ...draft, last_terms_verified: e.target.value })}
                className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
              />
            </label>
            <label className="block">
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#6E6A63]">
                Reapply date if rejected
              </span>
              <input
                type="date"
                value={(draft.reapply_date || "").slice(0, 10)}
                onChange={(e) => setDraft({ ...draft, reapply_date: e.target.value })}
                className="w-full border border-line px-3 py-2.5 mt-1.5 text-[13.5px] bg-white"
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-7">
            {BOOL_FIELDS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2.5 text-[13px] font-light text-[#5A5A5A]">
                <input
                  type="checkbox"
                  checked={!!draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })}
                  data-testid={`offer-bool-${k}`}
                  className="accent-[#7A8B73] w-4 h-4"
                />
                {label}
              </label>
            ))}
          </div>

          <div className="flex items-center gap-5 mt-8 pt-6 border-t border-line">
            <button
              onClick={() => save(openId, draft)}
              data-testid="offer-save"
              className="px-8 py-3.5 bg-charcoal text-white text-[11.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors"
            >
              Save offer
            </button>
            <p className="text-[12px] font-light text-[#6E6A63] max-w-md leading-relaxed">
              A public CTA only goes live when status is ACTIVE, Active is checked and a tracking URL is
              present. Anything else keeps the module in disabled preview state.
            </p>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
